import React from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from './ui/button';

/* PILOTO-1B (firma #80) — LA CONFIRMACIÓN BREVE DE «GENERAR CÓDIGO NUEVO».
 *
 * POR QUÉ EXISTE LA CONFIRMACIÓN: regenerar es IRREVERSIBLE y silencioso desde
 * afuera. El profesor que ya repartió el link por WhatsApp lo mata en el acto y
 * nadie se lo avisa a los alumnos que aún no entraban. Un clic accidental cuesta
 * una clase de confusión, así que el paso extra se paga solo. Lo que NO cuesta
 * nada es lo que hay que decir en el cuerpo: los que ya están adentro se quedan.
 *
 * POR QUÉ ES UN COMPONENTE COMPARTIDO Y NO DOS COPIAS: lo piden las DOS
 * pantallas de profesor (la tarjeta de la lista y el detalle). Con dos copias,
 * las reglas de forma —44 pt, la safe area de abajo, el `fixed` bajo el header—
 * se habrían desincronizado en el primer retoque.
 *
 * SAFE AREA: `fixed` al viewport → escribe la suya abajo (patrón
 * Dashboard.js:2994), igual que sus hermanos de PILOTO-1.
 */

export default function RegenerateCodeModal({ open, groupName, expired, onCancel, onConfirm, working }) {
  const { t } = useTranslation(['dashboard', 'common']);
  if (!open) return null;

  return (
    <div className="fixed top-14 inset-x-0 bottom-0 z-[60] bg-black/80 flex items-center justify-center p-4"
      data-testid="group-regenerate-modal">
      <div className="relative bg-[#111] border border-[#262626] rounded-[20px] p-6 sm:p-8 pb-[max(1.5rem,env(safe-area-inset-bottom,0px))] max-w-md w-full mx-4 shadow-2xl">
        <h3 className="text-lg font-semibold text-white mb-2">
          {t('dashboard:groups.regenerate_title')}
        </h3>
        {/* DE QUÉ GRUPO. En `/groups` hay una tarjeta por grupo y un solo modal
            compartido: sin el nombre, el profesor confirma a ciegas —y una
            pantalla que no puede decir sobre qué está actuando tampoco se puede
            probar—. Es la misma razón por la que el modal de «quitar alumno» de
            PILOTO-1 nombra al alumno. */}
        {groupName && (
          <p className="text-base font-semibold text-white truncate mb-2"
            data-testid="group-regenerate-target">
            {groupName}
          </p>
        )}
        {/* DOS CUERPOS, porque el hecho es distinto. Con el código VIGENTE la
            advertencia es real: se está matando algo que sirve. Con el código
            YA VENCIDO, decirle «dejarán de servir en el acto» sería mentirle —
            acaba de leer «este código ya no deja entrar a nadie» dos centímetros
            arriba— así que el cuerpo cambia a lo único que le importa: que el
            nuevo sirve ya, y que sus alumnos se quedan. */}
        <p className="text-sm text-[#a0a0a0] mb-5" data-testid="group-regenerate-body">
          {expired
            ? t('dashboard:groups.regenerate_body_expired')
            : t('dashboard:groups.regenerate_body')}
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Cancelar es el botón ACENTUADO, igual que en el modal de «quitar
              alumno» de PILOTO-1: en una confirmación destructiva el camino
              seguro es el que debe caer bajo el pulgar. */}
          <Button onClick={onCancel}
            className="flex-1 bg-[#FF6B00] hover:bg-[#E05A00] text-white rounded-xl min-h-[44px] text-sm"
            data-testid="group-regenerate-cancel">
            {t('common:cancel')}
          </Button>
          <Button variant="outline" onClick={onConfirm} disabled={working}
            className="flex-1 border-[#333] text-[#a0a0a0] hover:text-white hover:border-[#555] rounded-xl min-h-[44px] text-sm disabled:opacity-40"
            data-testid="group-regenerate-confirm">
            {working
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : t('dashboard:groups.regenerate_confirm_btn')}
          </Button>
        </div>
      </div>
    </div>
  );
}
